import { Component, ComponentWillLoad, h, JSX, State, Host, Prop, EventEmitter, Event, Listen } from '@stencil/core';
import { OcrDocument, OcrMutationVariables } from 'src/generated/operations';
import { ApolloProviderConsumer } from 'src/utils/apollo-client-state';
import { ApolloClient } from '@apollo/client/core';
import { injectHistory, RouterHistory } from '@stencil/router';
import { detectModule } from 'src/utils/detectModule';
import { MESSAGES, PATH } from '../constants';
import { store } from 'src/utils/store';
import { createClickHandlerForOCR } from 'src/utils/createClickHandlerForOCR';
import { OcrError } from 'src/types/OcrError';
import { resizeImage } from 'src/utils/resizeImage';
import { BelimoError } from '../app-error-messages/type';

@Component({
  tag: 'app-ocr',
  shadow: false,
})
export class AppOcr implements ComponentWillLoad {
  @Prop({mutable: true}) client: ApolloClient<any>;
  @Prop({mutable: true}) history: RouterHistory;
  @Event() belimoOcrError!: EventEmitter<OcrError>;
  @Event() belimoError!: EventEmitter<BelimoError>;
  @Event() belimoOcrParsingStart!: EventEmitter<string>;
  @Event() belimoOcrParsingEnd!: EventEmitter<any>;
  private fileInputClicked = false;
  private input: HTMLInputElement;
  private reader = new FileReader();
  private onLoadCallback = () => this.processFile();
  private handleClick: (e: MouseEvent) => void = () => null;
  @State() results: string[] = [];
  private gaInitTriggered:boolean = false;
  private errorTimeout: any = null;

  public async componentWillLoad(): Promise<void> {
    this.reader.addEventListener('load', this.onLoadCallback);
    const handler = createClickHandlerForOCR();
    this.handleClick = (e) => {
      this.fileInputClicked = true;
      return handler(e);
    }
  }

  public disconnectedCallback(): void {
    this.reader.removeEventListener('load', this.onLoadCallback);
    clearTimeout(this.errorTimeout);
  }

  @Listen('focus', { target: 'window' })
  trackCancelEvent() {
    if (!this.gaInitTriggered) {
      return;
    }
    // if user click on cancel button at files upload modal we will have focus event on window,
    // and we should send ocr error event,
    // but onChange event on file input should have a chance to cancel it
    this.errorTimeout = setTimeout(() => { this.trackCancel() }, 500);
  }

  public render(): JSX.Element {
    return (<Host>
      <label htmlFor={'ocr-input'}
             onClick={(e) => this.handleClick(e)}
             class="btn btn--primary btn--icon-left btn--bg">
        <input type="file" id={'ocr-input'} style={{display: 'none'}}
               onChange={(e) => this.onChange(e)}
               onClick={() => {
                 this.gaInitTriggered = true;
                 store.state.gaPush({
                   event: 'ga4_event',
                   event_name: 'retrofit_tool_ocr_init',
                   retrofit_tool_ocr_init: {
                     eventCategory: 'retrofit tool',
                     eventAction: 'ocr init',
                     eventLabel: undefined
                   }
                 });
               }}
               ref={(e) => this.input = e}
               accept="image/*"
        />
        <div class="btn__icon">
          <belimo-svg-sprite svgId={'scan'}/>
        </div>
        <div class="btn__content">
          <belimo-i18n messageId={MESSAGES.SCAN}/>
        </div>
      </label>
    </Host>);
  }

  @Listen('processOcrImage', {target: 'document'})
  public async processOcrImage({detail}: { detail: string }): Promise<void> {
    if (this.fileInputClicked === false) {
      return;
    }
    this.fileInputClicked = false;
    return this.detect(detail);
  }

  private onChange(e: any) {
    e.preventDefault();
    this.gaInitTriggered = false;
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = null;
    }
    const file = this.input.files.item(0);
    this.reader.readAsDataURL(file);
  }

  private async detect(image: string): Promise<void> {
    this.input.value = '';
    try {
      this.belimoOcrParsingStart.emit('');
      const payload = await resizeImage(window, image);
      this.belimoOcrParsingStart.emit(payload);
      const response = await this.client.mutate<any, OcrMutationVariables>({
        mutation: OcrDocument,
        variables: {
          payload,
          region: store.state.currentRegion,
          limit: 1,
        }
      })
      const [result = null] = response.data.ocr.results;
      if (result === null) {
        // send event with detected keywords to show feedback for the user
        this.belimoOcrError.emit({
          payload,
          keywords: response.data.ocr.keywords.length > 0 ? response.data.ocr.keywords : ['-'],
        });
        this.belimoOcrParsingEnd.emit();
        this.sendOcrError();
        return;
      }
      const module = detectModule(result.section, result.productType, result.category);
      if (module) {
        store.state.gaPush({
          event: 'ga4_event',
          event_name: 'retrofit_tool_ocr_complete',
          retrofit_tool_ocr_complete: {
            eventCategory: 'retrofit tool',
            eventAction: 'ocr complete',
            eventLabel: undefined
          }
        });
        this.gaInitTriggered = false;
        // redirect to first found product details page
        const url = `${store.state.root}${PATH.DETAILS}/${module}/${encodeURIComponent(result.ID)}/${encodeURIComponent(result.Label)}`;
        this.history.push(url);
      }
    } catch (e) {
      const type = store.state.enableOffline ? 'noConnection' : 'unknown';
      this.belimoError.emit({ type, cause: e });
      this.sendOcrError();
    }
    this.belimoOcrParsingEnd.emit();
  }

  private async processFile(): Promise<void> {
    if (this.reader.result === null) {
      console.error(this.reader.error);
      return;
    }
    await this.detect(this.reader.result?.toString());
  }

  private sendOcrError() {
    store.state.gaPush({
      event: 'ga4_event',
      event_name: 'retrofit_tool_ocr_error',
      retrofit_tool_ocr_error: {
        eventCategory: 'retrofit tool',
        eventAction: 'ocr error',
        eventLabel: undefined
      }
    });
  }

  private trackCancel() {
    if (!this.gaInitTriggered) {
      return;
    }
    store.state.gaPush({
      event: 'ga4_event',
      event_name: 'retrofit_tool_ocr_cancel',
      retrofit_tool_ocr_cancel: {
        eventCategory: 'retrofit tool',
        eventAction: 'ocr cancel',
        eventLabel: undefined
      }
    });
    this.gaInitTriggered = false;
  }
}

ApolloProviderConsumer.injectProps(AppOcr, ['client']);
injectHistory(AppOcr);
